import { FE_ERROR_CODES, FeErrorCode } from './errorCodes.ts';

export type MarketCode =
  | 'de' | 'en' | 'en-ca' | 'en-gb' | 'en-in' | 'en-nz' | 'es'
  | 'ie' | 'ja' | 'no' | 'row' | 'sv' | 'rog' | 'on' | 'fi';

enum Actions {
  OPEN_SUPPORT = 'open_support',
  REDIRECT_KYC = 'redirect_kyc',
  CLOSE_DIALOG = 'close_dialog',
  RESTART_FLOW = 'restart_flow',
  CLEAR_AND_CLOSE = 'clear_and_close',
  REDIRECT_LOGIN = 'redirect_login',
  RETRY_PNP_INIT = 'retry_pnp_init',
  RETRY_PNP_LOGIN = 'retry_pnp_login',
  BACK_TO_CASHIER = 'back_to_cashier',
  NOOP = 'noop',
}

export type ErrorIcon = 'warning' | 'success';

export interface ErrorUiConfig {
  /** Use a specific i18n key if it differs from the FE error key */
  i18nKey?: string;
  icon?: ErrorIcon;
  /** Hide the boundary entirely (forms handle it inline) */
  ignoreInBoundary?: boolean;
  skipPnpRemap?: boolean;
  /** Show these buttons (text comes from GraphQL; presence comes from config) */
  buttons?: {
    primary?: Actions;
    secondary?: Actions;
  };
}

type ErrorRegistry = Record<FeErrorCode, ErrorUiConfig>;

export const BASE_ERROR_REGISTRY: Partial<ErrorRegistry> = {
  [FE_ERROR_CODES.unknown_error]: {
    i18nKey: 'something_went_wrong',
    buttons: { primary: Actions.RESTART_FLOW },
  },

  [FE_ERROR_CODES.pnp_init_failed]: {
    i18nKey: 'pnp_something_went_wrong_pre_deposit',
    buttons: {
      primary:   Actions.RETRY_PNP_INIT,
      secondary: Actions.OPEN_SUPPORT,
    },
  },

  [FE_ERROR_CODES.pnp_login_failed]: {
    i18nKey:      'pnp_login_failed',
    icon:         'success',
    skipPnpRemap: true,
    buttons:      {
      primary: Actions.REDIRECT_LOGIN,
    },
  },

  [FE_ERROR_CODES.duplicate_email]:    { ignoreInBoundary: true },
  [FE_ERROR_CODES.duplicate_phone]:    { ignoreInBoundary: true },
  [FE_ERROR_CODES.invalid_postalcode]: { ignoreInBoundary: true },
  [FE_ERROR_CODES.limit_too_low]:      { ignoreInBoundary: true },
  [FE_ERROR_CODES.limit_too_high]:     { ignoreInBoundary: true },
  [FE_ERROR_CODES.limit_required]:     { ignoreInBoundary: true },
  [FE_ERROR_CODES.invalid_identity]:   { ignoreInBoundary: true },

  [FE_ERROR_CODES.kyc_required]: {
    buttons: { primary: Actions.REDIRECT_KYC, secondary: Actions.OPEN_SUPPORT },
  },
  [FE_ERROR_CODES.registration_cancelled]: {
    buttons: { primary: Actions.OPEN_SUPPORT, secondary: Actions.CLOSE_DIALOG },
  },
  [FE_ERROR_CODES.credentials_taken]: {
    buttons: { primary: Actions.RESTART_FLOW, secondary: Actions.OPEN_SUPPORT },
  },
  [FE_ERROR_CODES.duplicate_account]: {
    buttons: { primary: Actions.OPEN_SUPPORT, secondary: Actions.CLOSE_DIALOG },
  },
  [FE_ERROR_CODES.user_under_age]: {
    skipPnpRemap: true,
    buttons:      { primary: Actions.CLOSE_DIALOG },
  },
  [FE_ERROR_CODES.unique_player]: {
    skipPnpRemap: true,
    buttons:      { primary: Actions.REDIRECT_LOGIN, secondary: Actions.OPEN_SUPPORT },
  },
  [FE_ERROR_CODES.id_verification_failed]: {
    buttons: { primary: Actions.OPEN_SUPPORT },
  },
  [FE_ERROR_CODES.country_restricted]: {
    skipPnpRemap: true,
    buttons:      { primary: Actions.CLOSE_DIALOG, secondary: Actions.OPEN_SUPPORT },
  },
  [FE_ERROR_CODES.self_excluded_with_national_registry]: {
    buttons: { primary: Actions.RESTART_FLOW, secondary: Actions.OPEN_SUPPORT },
  },
  [FE_ERROR_CODES.generic_error_message]: {
    i18nKey:      'generic_error_message',
    skipPnpRemap: true,
    buttons:      { primary: Actions.REDIRECT_LOGIN, secondary: Actions.OPEN_SUPPORT },
  },
  [FE_ERROR_CODES.incorrect_details]: {
    buttons: { primary: Actions.RESTART_FLOW },
  },
  [FE_ERROR_CODES.piq_payment_failed]: {
    i18nKey: 'piq_payment_failed',
    buttons: { primary: Actions.RESTART_FLOW, secondary: Actions.CLOSE_DIALOG },
  },
  [FE_ERROR_CODES.money_limit_exceeded]: {
    i18nKey: 'pnp_deposit_limit_exceeded',
    buttons: {
      primary:   Actions.BACK_TO_CASHIER,
      secondary: Actions.OPEN_SUPPORT,
    },
  },
  [FE_ERROR_CODES.player_matched_but_blocked]: {
    i18nKey:      'pnp_player_blocked',
    skipPnpRemap: true,
    buttons:      { primary: Actions.OPEN_SUPPORT },
  },
  [FE_ERROR_CODES.player_not_matched]: {
    i18nKey: 'pnp_data_mismatch',
    buttons: { primary: Actions.OPEN_SUPPORT },
  },
  [FE_ERROR_CODES.multiple_pno_usage]: {
    i18nKey: 'pnp_multiple_accounts',
    buttons: { primary: Actions.OPEN_SUPPORT },
  },
  [FE_ERROR_CODES.registration_timed_out]: {
    i18nKey: 'registration_timed_out',
    buttons: {
      primary: Actions.BACK_TO_CASHIER,
    },
  },
  [FE_ERROR_CODES.piq_transaction_refund]: {
    i18nKey: 'piq_transaction_refund',
    buttons: {
      primary:   Actions.BACK_TO_CASHIER,
      secondary: Actions.OPEN_SUPPORT,
    },
  },
  [FE_ERROR_CODES.pnp_deposit_unsuccessful]: {
    i18nKey: 'pnp_deposit_unsuccessful',
    buttons: {
      primary:   Actions.BACK_TO_CASHIER,
      secondary: Actions.OPEN_SUPPORT,
    },
  },
} as const;

/** Optional per-market overrides */
export const MARKET_ERROR_OVERRIDES: Partial<Record<string, Partial<ErrorRegistry>>> = {
  ie: {
    [FE_ERROR_CODES.kyc_required]: {
      buttons: { primary: Actions.REDIRECT_KYC },
    },
  },
  fi: {
    [FE_ERROR_CODES.kyc_required]: {
      buttons: { primary: Actions.REDIRECT_KYC },
    },
  },
  sv: {
    [FE_ERROR_CODES.unknown_error]: {
      i18nKey: 'something_went_wrong',
      buttons: { primary: Actions.OPEN_SUPPORT },
    },
  },
};

const remapForPnp = (action?: Actions): Actions | undefined => {
  if (!action || action === Actions.OPEN_SUPPORT || action === Actions.RETRY_PNP_INIT) return action;

  return Actions.BACK_TO_CASHIER;
};

export const resolveErrorConfig = (
  market: string,
  code: FeErrorCode,
  isPnp = false,
): ErrorUiConfig => {
  const base = BASE_ERROR_REGISTRY[code] ?? BASE_ERROR_REGISTRY[FE_ERROR_CODES.unknown_error];
  const override = MARKET_ERROR_OVERRIDES[market]?.[code];
  const config = { ...base, ...(override ?? {}) };

  if (!isPnp || !config.buttons || config.skipPnpRemap) return config;

  const primary = remapForPnp(config.buttons.primary);
  const secondary = remapForPnp(config.buttons.secondary);

  return {
    ...config,
    buttons: {
      primary,
      secondary: secondary === primary ? undefined : secondary,
    },
  };
};
